/**
 * Shared report freshness bar + Rebuild (WSRADM-0021).
 *
 * Renders the three server-sent states as sent — never infers staleness from
 * the clock. Rebuild is a one-shot trigger; the parent owns the POST and must
 * not poll or refetch on a timer.
 */
import { Alert, Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';

export interface ReportFreshnessProps {
  asOf: string;
  stale: boolean;
  neverComputed: boolean;
  timezone: string;
  /** Shown only to OWNER/ADMIN. */
  canRebuild: boolean;
  onRebuild: () => void;
  rebuilding: boolean;
}

function formatAsOf(asOf: string, timezone: string, locale: string): string {
  if (!asOf) return '';
  const parsed = new Date(asOf);
  if (Number.isNaN(parsed.getTime())) return asOf;
  const options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' };
  if (timezone.length > 0) {
    try {
      return new Intl.DateTimeFormat(locale, { ...options, timeZone: timezone }).format(parsed);
    } catch {
      // Invalid timezone — format without it. Not a staleness decision.
    }
  }
  return new Intl.DateTimeFormat(locale, options).format(parsed);
}

export function ReportFreshness({
  asOf,
  stale,
  neverComputed,
  timezone,
  canRebuild,
  onRebuild,
  rebuilding,
}: ReportFreshnessProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const formatted = formatAsOf(asOf, timezone, i18n.language);
  const rebuildButton = canRebuild ? (
    <Button
      size="small"
      variant="outlined"
      onClick={onRebuild}
      disabled={rebuilding}
      sx={{ ml: 1 }}
    >
      {t('reports.freshness.rebuild', 'Rebuild')}
    </Button>
  ) : null;

  if (neverComputed) {
    return (
      <Alert severity="info" sx={{ mb: 2 }} action={rebuildButton}>
        {t('reports.freshness.neverComputed', 'This report has not been built yet.')}
      </Alert>
    );
  }

  if (stale) {
    return (
      <Alert severity="info" sx={{ mb: 2 }} action={rebuildButton}>
        {formatted
          ? t(
              'reports.freshness.stale',
              'These numbers are as of {{asOf}}. A recompute is pending.',
              { asOf: formatted },
            )
          : t('reports.freshness.stalePending', 'A recompute is pending.')}
      </Alert>
    );
  }

  // Hide the as-of caption when asOf is null/empty (series payload may omit it).
  if (!formatted && !rebuildButton) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
      {formatted ? (
        <Typography variant="caption" color="text.secondary">
          {t('reports.freshness.asOf', 'as of {{asOf}}', { asOf: formatted })}
        </Typography>
      ) : null}
      {rebuildButton}
    </Box>
  );
}
